/* eslint-disable no-unused-expressions */
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import pq from "pqgrid";
import "pqgrid/localize/pq-localize-kr.js";
import "./PqGrid.css";

// pq.aggregate.custom = function (arr, col) {
//   return pq.formatNumber(pq.aggregate.sum(arr, col), "#,###");
// };

const PqGrid = forwardRef((props, ref) => {
  const refGrid = useRef();
  const [grid, setGrid] = useState(null);
  const gridRef = useRef(null);
  const history = useRef(null);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const updateDimensions = () => {
    setDimensions({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  useEffect(() => {
    window.addEventListener("resize", updateDimensions);
    window.addEventListener("dblclick", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
      window.removeEventListener("dblclick", updateDimensions);
    };
  }, []);

  useEffect(() => {
    if (refGrid.current) {
      refGrid.current.style.height = `${dimensions.height - 200}px`;
    }
  }, [dimensions]);

  useEffect(() => {
    const shortCut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "r") {
        event.preventDefault();
        if (props.display) {
          props.retrieve?.();
        }
      }
    };

    window.addEventListener("keydown", shortCut);

    return () => {
      window.removeEventListener("keydown", shortCut);
    };
  }, [props.display]);

  const defaultOption = {
    collapsible: { on: false, toggle: true },
    showTitle: false,
    showHeader: true,
    menuIcon: true,
    locale: "kr",
    height: "100%",
    numberCell: {
      show: true,
      width: 35,
      title: "No",
    },
    stripeRows: false,
    rowBorders: true,
    animModel: { on: false },
    groupable: true,
    groupModel: {
      on: true,
      menuItems: ["merge", "grandSummary"],
      summaryInTitleRow: "all",
      merge: true,
      grandSummary: true,
    },
    columnTemplate: { width: 100 },
    colModel: [],
    dataModel: {
      data: [],
    },
    trackModel: { on: true },
    showToolbar: true,
    virtualX: false,
    virtualY: true,
    summaryTitle: {
      avg: "평균 {0}",
      count: "({0})",
      max: "최대값 {0}",
      min: "최소값 {0}",
      sum: "{0}",
    },
  };

  useEffect(() => {
    if (props.display) {
      if (!grid) {
        let length = props.columns.length - 1;
        let colModel = props.columns.map((col, i) => ({
          ...col,
          filter: {
            crules: [{ condition: "range" }],
          },
          valign: "top",
          style: {
            ...col.style,
            "border-right": length == i ? "none !important" : "",
          },
          styleHead: {
            ...col.styleHead,
            "text-align": "center",
            "background-color": "#4A5863",
            color: "#ffffff",
            "font-size": "13px",
            padding: "4px 0px",
            "font-weight": "700",
          },
          editable: false,
          render: (ui) => {
            const { rowIndx, colIndx, column } = ui;
            var grid = gridRef.current;

            if (grid && column.suppress) {
              const nextRowData = grid.getRowData({ rowIndx: rowIndx + 1 });

              if (nextRowData && !nextRowData[column.dataIndx]) {
                return {
                  style:
                    "border-bottom: 1px solid rgb(255,255,255); box-shadow : #cbcbcb -1px 0px 0px 0px;",
                };
              }
            }
          },
        }));
        let temp = {
          ...defaultOption,
          colModel: colModel,
          contextMenu: {
            on: true,
            cellItems: contextItems,
          },
          selectChange: (event, ui) => {
            var grid = gridRef.current,
              addr = ui.selection.address()[0],
              row = addr?.r1 ?? 0;

            props.onRowClick?.(grid.getRowData({ rowIndx: row }));
          },
          editable: false,
        };
        /* eslint-disable no-unused-expressions */
        const _grid = pq.grid(refGrid.current, temp);
        setGrid(_grid);
        gridRef.current = _grid;
        history.current = _grid.History();
      }
    }
  }, [props.display, props.columns, grid]);

  useEffect(() => {
    if (grid) {
      grid.options.dataModel.data = props.dataModel;
      grid.refreshView();
      autoMerge();
      // autoSuppress();
      props.onSetData?.();
    }
  }, [grid, props.dataModel]);

  useEffect(() => {
    if (grid) {
      grid.Group().option({
        on: props.useGroupingPanel,
      });
    }
  }, [grid]);

  useEffect(() => {
    if (ref && grid) {
      ref.current = {
        exportExcel: exportExcel,
        setSelection: setSelection,
        refreshView: () => grid.refreshView(),
        destroy: () => {
          grid.destroy();
          setGrid(null);
        },
      };
    }
  }, [ref, grid]);

  const contextItems = (evt, ui) => {
    return [
      {
        name: "컬럼 숨기기",
        action: (evt, ui) => {
          var grid = gridRef.current,
            di = [],
            addr = grid.Selection().address()[0],
            c1 = addr.c1,
            c2 = addr.c2,
            CM = grid.getColModel();

          for (var c = c1; c <= c2; c++) {
            di.push(CM[c].dataIndx);
          }
          grid.Columns().hide({
            diHide: di,
          });
        },
      },
      {
        name: "컬럼 숨기기 취소",
        action: (evt, ui) => {
          var grid = gridRef.current,
            di = [],
            CM = grid.getColModel();
          for (var c = 0; c < CM.length; c++) {
            di.push(CM[c].dataIndx);
          }
          grid.Columns().hide({
            diShow: di,
          });
        },
      },
      "separator",
      {
        name: "열 고정",
        disabled:
          gridRef.current?.option("freezeCols") ==
          gridRef.current?.Selection()?.address()[0]?.c1 + 1,
        action: (evt, ui) => {
          var grid = gridRef.current,
            addr = grid.Selection().address()[0],
            c1 = addr.c1 + 1;
          grid.option("freezeCols", c1);
          grid.refresh();
        },
      },
      {
        name: "열 고정 해제",
        disabled: gridRef.current?.option("freezeCols") == 0,
        action: (evt, ui) => {
          var grid = gridRef.current;
          grid.option("freezeCols", 0);
          grid.refresh();
        },
      },
      {
        name: "행 고정",
        disabled:
          gridRef.current?.option("freezeRows") ==
          gridRef.current?.Selection()?.address()[0]?.r1 + 1,
        action: (evt, ui) => {
          var grid = gridRef.current,
            addr = grid.Selection().address()[0],
            r1 = addr.r1 + 1;
          grid.option("freezeRows", r1);
          grid.refresh();
        },
      },
      {
        name: "행 고정 해제",
        disabled: gridRef.current?.option("freezeRows") == 0,
        action: (evt, ui) => {
          var grid = gridRef.current;
          grid.option("freezeRows", 0);
          grid.refresh();
        },
      },
      {
        name: "행열 고정",
        disabled:
          gridRef.current?.option("freezeCols") ==
            gridRef.current?.Selection()?.address()[0]?.c1 + 1 &&
          gridRef.current?.option("freezeRows") ==
            gridRef.current?.Selection()?.address()[0]?.r1 + 1,
        action: (evt, ui) => {
          var grid = gridRef.current,
            addr = grid.Selection().address()[0],
            c1 = addr.c1 + 1,
            r1 = addr.r1 + 1;
          grid.option("freezeCols", c1);
          grid.option("freezeRows", r1);
          grid.refresh();
        },
      },
      {
        name: "행열 고정 해제",
        disabled:
          gridRef.current?.option("freezeCols") == 0 &&
          gridRef.current?.option("freezeRows") == 0,
        action: (evt, ui) => {
          var grid = gridRef.current;
          grid.option("freezeCols", 0);
          grid.option("freezeRows", 0);
          grid.refresh();
        },
      },
      "separator",
      {
        name: "복사하기",
        shortcut: "Ctrl + C",
        action: function () {
          gridRef?.current?.copy();
        },
      },
      // {
      //   name: "변경정보 확인",
      //   action: function () {
      //     console.log(gridRef.current?.isDirty);
      //     console.log(gridRef.current?.getChanges({ format: "byVal" }));
      //   },
      // },
      // {
      //   name: "변경상태 리셋",
      //   action: function () {
      //     history.current?.reset();
      //   },
      // },
      // {
      //   name: "되돌리기",
      //   action: (evt, ui) => {
      //     if (history.current) {
      //       history.current.undo();
      //     }
      //   },
      // },
      // {
      //   name: "다시 실행",
      //   action: (evt, ui) => {
      //     if (history.current) {
      //       history.current.redo();
      //     }
      //   },
      // },
    ];
  };

  const mergeCells = () => {
    var grid = gridRef.current;
    var data = grid.option("dataModel.data");
    var mergeCells = [];
    var prevJobNo = "";
    var startRow = 0;
    var CM = grid.getColModel();

    for (var i = 0; i < data.length; i++) {
      var currJobNo = data[i].JOB_NO;
      if (currJobNo !== prevJobNo) {
        if (i > 0) {
          // Merge cells for JOB_NO
          mergeCells.push({ r1: startRow, c1: 0, r2: i - 1, c2: 0 });
          // Merge cells for other columns if data is empty
          for (var j = 1; j < CM.length; j++) {
            var isColumnEmpty = true;
            for (var k = startRow; k < i; k++) {
              if (data[k][CM[j].dataIndx]) {
                isColumnEmpty = false;
                break;
              }
            }
            if (isColumnEmpty) {
              mergeCells.push({ r1: startRow, c1: j, r2: i - 1, c2: j });
            }
          }
        }
        startRow = i;
        prevJobNo = currJobNo;
      }
    }
    // Merge cells for the last group
    mergeCells.push({ r1: startRow, c1: 0, r2: data.length - 1, c2: 0 });
    for (var j = 1; j < CM.length; j++) {
      var isColumnEmpty = true;
      for (var k = startRow; k < data.length; k++) {
        if (data[k][CM[j].dataIndx]) {
          isColumnEmpty = false;
          break;
        }
      }
      if (isColumnEmpty) {
        mergeCells.push({ r1: startRow, c1: j, r2: data.length - 1, c2: j });
      }
    }

    grid.option("mergeCells", mergeCells);
    grid.refreshView();
  };

  const autoMerge = () => {
    var grid = gridRef.current;
    var mc = [],
      CM = grid.option("colModel"),
      i = CM.length,
      data = grid.option("dataModel.data");

    CM.forEach((col, i) => {
      if (col.merge) {
        // Check if the 'merge' key is true
        var dataIndx = col.dataIndx,
          rc = 1,
          j = data.length;

        while (j--) {
          var cd = data[j][dataIndx],
            cd_prev = data[j - 1] ? data[j - 1][dataIndx] : undefined;
          if (cd_prev !== undefined && cd == cd_prev) {
            rc++;
          } else if (rc > 1) {
            mc.push({ r1: j, c1: i, rc: rc, cc: 1 });
            rc = 1;
          }
        }
      }
    });

    grid.option("mergeCells", mc);
    grid.refreshView();
  };

  const exportExcel = (fileName) => {
    if (grid) {
      let blob = grid.exportData({
        format: "xlsx",
        skipHiddenCols: true,
        skipHiddenRows: false,
        selection: false,
        cssRules: "td{font-size:11 !important;}",
      });
      pq.saveAs(blob, fileName + ".xlsx");
    }
  };

  const setSelection = (row, col) => {
    if (row >= 0 && col >= 0) {
      grid.setSelection({ rowIndx: row, colIndx: col });
    } else if (row >= 0) {
      grid.setSelection({ rowIndx: row });
    }
  };

  return (
    <div
      className="pqgrid-container"
      style={{
        width: `${props.width}`,
        height: "100%",
      }}
    >
      <div
        ref={refGrid}
        style={{
          width: "100%",
          height: "100%",
          paddingBottom: "2px",
        }}
      />
    </div>
  );
});

export default PqGrid;
